import moment from "moment";
import { getClientDisplayName } from "@modules/advisor/helper";
import { readableDate } from "../../helpers/DateFormat";
import {
  CLOSED_WORK_ITEM_STAGES,
  OUTLOOK_COMPOSE_URL,
  RECENT_DAYS,
  UPCOMING_DAYS,
} from "./constants";
import {
  CommandCenterWorkItem,
  MeetingBrief,
  MeetingRecap,
  RecapButtonState,
  ScheduleAttendee,
  ScheduleMeeting,
} from "./types";

// Earned's current, legacy (Forme) and affiliate-firm domains. Attendees on
// these domains are colleagues, not clients — including anyone who is also a
// client in their own right, whose household must not be inferred from an
// internal invite. Kept as one list so adding a domain is a one-liner.
const INTERNAL_EMAIL_DOMAINS = [
  "@earned.com",
  "@formefinancial.com",
  "@dmcounsel.com",
];

const isInternalEmail = (email?: string): boolean => {
  const normalized = email?.toLowerCase() ?? "";
  return INTERNAL_EMAIL_DOMAINS.some((domain) => normalized.endsWith(domain));
};

// Fallback client identity when the household hasn't resolved: the first
// external attendee (never the organizer or an internal colleague), so a header
// never shows an advisor. Best-effort only — if a meeting has more than one
// external party (e.g. an accountant or attorney), this may pick the wrong one;
// the resolved household (brief.household_id) is the source of truth.
const externalAttendee = (
  meeting: ScheduleMeeting
): ScheduleAttendee | undefined =>
  meeting.attendees.find(
    (a) =>
      a.email &&
      a.email !== meeting.organizer_email &&
      !isInternalEmail(a.email)
  );

// "1:00 PM–1:50 PM" in the viewer's local zone. Split out from meetingLine so
// the printable brief can show the times without the subject prepended — there
// the subject is already the page heading.
const meetingTimeRange = (meeting: ScheduleMeeting): string =>
  `${moment(meeting.starts_at).format("h:mm A")}–${moment(
    meeting.ends_at
  ).format("h:mm A")}`;

// "<subject> · 1:00 PM–1:50 PM" — the one-line meeting summary in the pre-brief
// modal header.
const meetingLine = (meeting: ScheduleMeeting): string =>
  `${meeting.subject} · ${meetingTimeRange(meeting)}`;

interface NameParts {
  FIRST_NAME?: string;
  LAST_NAME?: string;
}

// "First Last" from name parts, or "" when neither is present. Reuses the
// advisor helper; we pass no EMAIL so it never falls back to an email here.
const fullName = (parts?: NameParts): string => getClientDisplayName(parts, "");

// Household header name: single client -> "First Last"; a couple sharing a last
// name -> "First1 & First2 Last"; otherwise "First1 Last1 & First2 Last2".
// Returns "" when no names are available so callers can fall back to another
// label (e.g. the backend household_name or the meeting subject).
const householdDisplayName = (
  client?: NameParts,
  coClient?: NameParts
): string => {
  const primary = fullName(client);
  // A co-client only counts as a second person when they have a first name;
  // otherwise a last-name-only record renders as a fragment ("Eric & Martinez").
  if (!coClient?.FIRST_NAME) return primary;
  const secondary = fullName(coClient);
  if (!primary) return secondary;
  if (
    client?.FIRST_NAME &&
    client.LAST_NAME &&
    client.LAST_NAME === coClient.LAST_NAME
  ) {
    return `${client.FIRST_NAME} & ${coClient.FIRST_NAME} ${client.LAST_NAME}`;
  }
  return `${primary} & ${secondary}`;
};

// Whole years from a DOB string; null when missing, unparseable, or nonsensical.
const ageFromDob = (dob?: string): number | null => {
  if (!dob) return null;
  const born = moment(dob);
  if (!born.isValid()) return null;
  const years = moment().diff(born, "years");
  return years >= 0 && years < 130 ? years : null;
};

// "City, ST" — drops a missing part; "" when both are empty.
const locationLabel = (city?: string, state?: string): string =>
  [city, state].filter((part) => part && part.trim()).join(", ");

interface ScheduleDay {
  key: string; // YYYY-MM-DD (local)
  label: string; // "Today" / "Tomorrow" / weekday
  meetings: ScheduleMeeting[];
}

// Saturday (6) or Sunday (0) for a YYYY-MM-DD key. moment is the repo's only
// date library, so no date-fns isWeekend here.
const isWeekendKey = (key: string): boolean =>
  [0, 6].includes(moment(key).day());

// One entry per day starting today (inclusive), each labeled "Today"/"Tomorrow"/
// weekday and carrying that day's meetings (empty array when none). Meetings are
// bucketed by their local calendar day and keep the backend's starts_at order.
// NOTE: meetings outside the `days` window are dropped, so `days` must match the
// query's fetch window — both default to UPCOMING_DAYS to keep them coupled.
// Empty weekend days are dropped afterwards (advisors rarely book them, so they
// were mostly dead "No meetings." rows). Filtering happens AFTER bucketing so the
// fetch window stays UPCOMING_DAYS-wide — a weekend meeting is still fetched, and
// its day still renders. This applies to today too: an empty Saturday today is
// hidden and the list starts at the next visible day.
const buildUpcomingDays = (
  meetings: ScheduleMeeting[],
  days = UPCOMING_DAYS
): ScheduleDay[] => {
  const byKey = new Map<string, ScheduleMeeting[]>();
  for (const meeting of meetings) {
    const key = moment(meeting.starts_at).format("YYYY-MM-DD");
    const bucket = byKey.get(key);
    if (bucket) bucket.push(meeting);
    else byKey.set(key, [meeting]);
  }

  return Array.from({ length: days }, (_, i) => {
    const date = moment().add(i, "days");
    const key = date.format("YYYY-MM-DD");
    return {
      key,
      label: readableDate(date.toDate()),
      meetings: byKey.get(key) ?? [],
    };
  }).filter((day) => day.meetings.length > 0 || !isWeekendKey(day.key));
};

// Day sections walking BACKWARDS from today, most recent first — within a day
// too, since looking back the meeting you just left is the one to act on.
// Meetings that haven't started are dropped ("today up to the current time").
// `days` must match the query's fetch window, as in buildUpcomingDays. `now` is
// injectable so tests can pin the clock.
const buildRecentDays = (
  meetings: ScheduleMeeting[],
  days = RECENT_DAYS,
  now = moment()
): ScheduleDay[] => {
  const byKey = new Map<string, ScheduleMeeting[]>();
  for (const meeting of meetings) {
    // Not yet started — not a "past meeting" yet.
    if (moment(meeting.starts_at).isAfter(now)) continue;
    const key = moment(meeting.starts_at).format("YYYY-MM-DD");
    const bucket = byKey.get(key);
    if (bucket) bucket.push(meeting);
    else byKey.set(key, [meeting]);
  }

  return Array.from({ length: days }, (_, i) => {
    const date = moment(now).subtract(i, "days");
    const key = date.format("YYYY-MM-DD");
    return {
      key,
      label: readableDate(date.toDate()),
      // Buckets are built here, so sorting one can't reach the caller's array.
      meetings: (byKey.get(key) ?? []).sort(
        (a: ScheduleMeeting, b: ScheduleMeeting) =>
          moment(b.starts_at).valueOf() - moment(a.starts_at).valueOf()
      ),
    };
  }).filter((day) => day.meetings.length > 0 || !isWeekendKey(day.key));
};

// No record means no transcript is matched, which is why "unavailable" is
// distinct from "generate". "error" falls back to "generate" so a failed run is
// retryable, as toPrepState does for briefs.
//
// "ready" requires recap_content, not just the status: the Meeting Assistant
// gates its Post-Meeting tab on the content being there, so a record marked
// ready with an empty body would otherwise render a button that opens a modal
// with that tab disabled. Treated as "generate" instead, which is both honest
// and retryable.
const toRecapButtonState = (recap?: MeetingRecap): RecapButtonState => {
  if (!recap) return "unavailable";
  if (recap.recap_status === "ready" && recap.recap_content) return "ready";
  if (recap.recap_status === "processing") return "processing";
  return "generate";
};

// Strips the Markdown an email composer won't render. Deliberately minimal: it
// unwraps only what the recap-email prompt emits, not general Markdown.
const markdownToPlainText = (markdown: string): string =>
  markdown
    .replace(/^#{1,6}\s+/gm, "")
    // `[ \t]*` not `\s*`: with the m flag, `\s*` reaches back across the
    // preceding newline and swallows the blank line that separates a paragraph
    // from the list under it.
    .replace(/^[ \t]*[-*][ \t]+/gm, "• ")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/(^|[^*])\*([^*]+)\*/g, "$1$2")
    .replace(/`([^`]+)`/g, "$1")
    // Keep the label, drop the URL syntax.
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

// URLSearchParams encodes spaces as "+", which both mailto: handlers and
// Outlook Web render as a literal plus in the subject line — "Follow-Up+&+Next
// +Steps". Percent-encode them instead, despite what the Outlook deep-link docs
// imply.
const composeQuery = (params: URLSearchParams): string =>
  params.toString().replace(/\+/g, "%20");

// Recipient and subject only. A body in the URL silently loses its tail to
// length limits, which on a client-facing email means sending half a recap.
// An absent `to` opens an empty recipient, never the literal "undefined".
//
// The recipient is encoded by hand rather than through composeQuery because in
// a mailto: URL it is part of the PATH, not the query string — URLSearchParams
// has nothing to say about it. buildOutlookComposeHref below puts `to` in the
// query string, so there it goes through the params like any other field.
const buildMailtoHref = ({
  to,
  subject,
}: {
  to?: string;
  subject: string;
}): string => {
  const query = composeQuery(new URLSearchParams({ subject }));
  return `mailto:${to ? encodeURIComponent(to) : ""}?${query}`;
};

// The Outlook Web App compose deep link.
const buildOutlookComposeHref = ({
  to,
  subject,
}: {
  to?: string;
  subject: string;
}): string => {
  const params = new URLSearchParams({ subject });
  if (to) params.set("to", to);
  return `${OUTLOOK_COMPOSE_URL}?${composeQuery(params)}`;
};

// Falls back to starts_at when ends_at is absent, because the print view
// synthesizes stub meetings with empty times.
const hasMeetingEnded = (
  meeting: Pick<ScheduleMeeting, "starts_at" | "ends_at">,
  now = moment()
): boolean => {
  const end = meeting.ends_at || meeting.starts_at;
  if (!end) return false;
  return moment(end).isBefore(now);
};

// Compact USD for the Practice AUM tile: "$413M", "$1.2M", "$950K", "$0".
// `Number(...toFixed(1))` trims a trailing ".0" so round values read cleanly
// ("$5M", not "$5.0M").
const AUM_BANDS: [number, string][] = [
  [1_000_000_000, "B"],
  [1_000_000, "M"],
  [1_000, "K"],
];

const formatAum = (dollars: number): string => {
  const sign = dollars < 0 ? "-" : "";
  const abs = Math.abs(dollars);

  for (let i = 0; i < AUM_BANDS.length; i++) {
    const [divisor, suffix] = AUM_BANDS[i];
    if (abs < divisor) continue;
    let value = Number((abs / divisor).toFixed(1));
    let unit = suffix;
    // Rounding to one decimal can push a value to 1000 of its unit (e.g.
    // 999,990,000 -> "1000.0M"); promote to the next-larger unit so we render
    // "$1B", never "$1000M".
    if (value >= 1000 && i > 0) {
      value = Number((abs / AUM_BANDS[i - 1][0]).toFixed(1));
      unit = AUM_BANDS[i - 1][1];
    }
    return `${sign}$${value}${unit}`;
  }
  return `${sign}$${Math.round(abs)}`;
};

// Time-of-day greeting for the header, bucketed by the given moment's local
// hour: morning (<12), afternoon (<17), otherwise evening.
const greeting = (now: moment.Moment): string => {
  const hour = now.hour();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

// Header subtitle: "Thursday, June 4, 2026 · 5 meetings and 9 tasks on deck."
// Both counts are pluralized; the segments mirror the mockup (team/pod dropped
// as there's no data source for it yet).
const buildSubtitle = (
  now: moment.Moment,
  meetingsToday: number,
  tasksDue: number
): string => {
  const plural = (count: number, noun: string): string =>
    `${count} ${noun}${count === 1 ? "" : "s"}`;
  return `${now.format("dddd, MMMM D, YYYY")} · ${plural(
    meetingsToday,
    "meeting"
  )} and ${plural(tasksDue, "task")} on deck.`;
};

// Best-effort client label for a meeting row in the Upcoming briefs list. Mirrors
// BriefModal's identity precedence at the list level (no household_id to resolve):
// the first external, non-organizer attendee's name (or email), then the brief's
// resolved household_name if one exists, else "" so the row can omit the column.
const meetingClientLabel = (
  meeting: ScheduleMeeting,
  brief?: MeetingBrief
): string => {
  const external = externalAttendee(meeting);
  return external?.name || external?.email || brief?.household_name || "";
};

// Visual tone for a Cases & tasks row's due badge.
type DueTone = "overdue" | "today" | "upcoming";

interface DueBadgeInfo {
  // "2d overdue" | "Due today" | "Due Jul 17, 2026".
  label: string;
  tone: DueTone;
}

// Derive the due badge from an ISO due date, compared by calendar day in the
// browser's local zone so the time component never makes today read as overdue.
// Past -> "Nd overdue" (red), same day -> "Due today" (yellow), future ->
// "Due MMM D, YYYY" (gray). Missing/unparseable date -> null so the row omits it.
//
// `now` is injectable so callers that already have a reference instant can pass it
// and stay consistent with it. The Cases & Tasks due-date FILTER delegates its
// overdue/today decision here precisely so the filter and the badge can never
// disagree on a boundary date — that only holds if the same instant reaches both,
// which is why this parameter exists rather than reading the clock unconditionally.
const deriveDueBadge = (
  dueDate: string | null,
  now: moment.Moment = moment()
): DueBadgeInfo | null => {
  if (!dueDate) return null;
  const due = moment(dueDate);
  if (!due.isValid()) return null;

  const diffDays = due
    .clone()
    .startOf("day")
    .diff(now.clone().startOf("day"), "days");
  if (diffDays < 0) {
    return { label: `${Math.abs(diffDays)}d overdue`, tone: "overdue" };
  }
  if (diffDays === 0) {
    return { label: "Due today", tone: "today" };
  }
  return { label: `Due ${due.format("MMM D, YYYY")}`, tone: "upcoming" };
};

interface StageProgress {
  // -1 when the stage isn't on the path.
  activeStep: number;
  isOffPath: boolean;
  // The real Salesforce stage, for the off-path badge. "" when none was sent.
  label: string;
}

// Resolve a Salesforce stage against an ordered path. Off-path is the normal case, not
// an error — Salesforce holds far more stages than a linear stepper can represent — so
// it returns -1 rather than clamping to step 0 and claiming progress the item hasn't
// made.
const resolveStageProgress = (
  stage: string | null,
  stages: readonly string[]
): StageProgress => {
  const label = (stage ?? "").trim();
  const activeStep = label ? stages.indexOf(label) : -1;
  return { activeStep, isOffPath: activeStep === -1, label };
};

// Drop terminal (completed/closed/canceled) cases or tasks — the Cases & tasks
// card lists OPEN work only, so closed items shouldn't show or count toward the
// tab totals (EPD-1349). Stage is compared case-insensitively.
// Generic so a caller's richer row type survives the filter.
const excludeClosedWorkItems = <T extends Pick<CommandCenterWorkItem, "stage">>(
  items: T[]
): T[] =>
  items.filter(
    // Coalesce: a blank Stage__c exists in Salesforce, and a bare .trim() on null
    // would throw and blank the whole list.
    (item) =>
      !CLOSED_WORK_ITEM_STAGES.has((item.stage ?? "").trim().toLowerCase())
  );

export {
  isInternalEmail,
  externalAttendee,
  meetingTimeRange,
  meetingLine,
  householdDisplayName,
  ageFromDob,
  locationLabel,
  buildUpcomingDays,
  formatAum,
  greeting,
  buildSubtitle,
  meetingClientLabel,
  deriveDueBadge,
  resolveStageProgress,
  excludeClosedWorkItems,
  buildRecentDays,
  toRecapButtonState,
  markdownToPlainText,
  buildMailtoHref,
  buildOutlookComposeHref,
  hasMeetingEnded,
};
export type { ScheduleDay, DueTone, DueBadgeInfo, StageProgress };
